import { Component, HostListener, OnInit } from '@angular/core'
import { NavigationEnd, Router, RouterOutlet } from '@angular/router'
import { EpicAppComponent } from '@core/components/app/app.component'
import { InternalServerErrorPage } from '@app/common-pages/internal-server-error/internal-server-error.page'
import { CommandLineComponent } from '@app/system/command-line/command-line.component'
import { NotificationService } from '@core/services/notification.service'
import { ModalService } from '@core/services/modal.service'
import { AppInfoComponent } from '@app/app/app-info.component'
import { LanguageService } from '@core/services/language.service'
import { config } from '@config'
import { LanguageSwitcherComponent } from '@core/components/language-switcher/language-switcher.component'
import { LanguageCode } from '@core/enums/language-code.enum'
import { MenuService } from '@core/services/menu.service'
import { AppService } from '@app/general/services/app.service'
import { LoaderService } from '@core/services/loader.service'
import { AuthService } from '@app/auth/services/auth.service'
import { SocketsService } from '@app/general/services/sockets.service'
import { filter, Subscription } from 'rxjs'
import { RegulationsForm } from '@app/settings/forms/user-regulations/user-regulations.form'
import { EquipmentsService, EquipmentUsePolicyAcceptanceRead } from '@app/generated'
import { ChatModule } from '@app/chat/chat.module'
import { TokenRequest, TokenResponse } from '@app/auth/models/token.model'
import { Logger } from '@app/general/utils/logger.util'
import { ChatService } from '@app/chat/services/chat.service'
import { RouterService } from '@app/general/services/router.service'
import { OrderListScrollStorageKey } from '@app/orders/components/order-list/order-list.component'
import { CashDisbursementsListScrollStorageKey } from '@app/cash-documents/pages/cash-disbursement/cash-disbursement/cash-disbursement.page'
import {
	CashReceiptsListScrollStorageKey,
	TransferListScrollStorageKey,
} from '@app/cash-documents/pages/cash-receipts/cash-receipts/cash-receipts.page'
import { InvoiceListScrollStorageKey } from '@app/invoices/pages/invoices/invoices.page'
import { CostInvoiceListScrollStorageKey } from '@app/cost-invoices/pages/cost-invoices/cost-invoices.page'
import { MailListScrollStorageKey } from '@app/mailbox/components/default-mail-page/default-mail-page.component'

@Component({
	selector: 'app-root',
	standalone: true,
	imports: [
		RouterOutlet,
		EpicAppComponent,
		InternalServerErrorPage,
		CommandLineComponent,
		LanguageSwitcherComponent,
		ChatModule,
	],
	template: `
		<epic-language-switcher (change)="changeLanguage($event)" />
		@if (showCommandLine) {
			<erp-command-line
				(close)="showCommandLine = false"
				(command)="handleCommand($event)" />
		}
		@if (internalServerError) {
			<internal-server-error />
		}
		<epic-app>
			<router-outlet />
		</epic-app>
	`,
	styles: [],
})
export class AppComponent implements OnInit {
	showCommandLine: boolean = false
	internalServerError: boolean = false

	constructor(
		private languageService: LanguageService,
		private notificationService: NotificationService,
		private modalService: ModalService,
		private menuService: MenuService,
		private appService: AppService,
		private loaderService: LoaderService,
		private authService: AuthService,
		private socketsService: SocketsService,
		private router: Router,
		private equipmentsService: EquipmentsService,
		private chatService: ChatService,
		private routerService: RouterService,
	) {
		/* DETERMINING KEY SHORTCUTS */
		/*		this.shortcutMap.set('Control.Shift.1', () => {
			this.routerService.navigateByUrl(`/${ config.modules.home.path }`)
		})
		this.shortcutMap.set('Control.Shift.@', () => {
			this.routerService.navigateByUrl(`/${ config.modules.orders.path }`)
		})
		this.shortcutMap.set('Control.Shift.3', () => {
			this.routerService.navigateByUrl(`/${ config.modules.contractors.path }/groups`)
		})
		this.shortcutMap.set('Control.Shift.4', () => {
			this.routerService.navigateByUrl(`/${ config.modules.contractors.path }`)
		})
		this.shortcutMap.set('Control.Shift.5', () => {
			this.routerService.navigateByUrl(`/${ config.modules.invoices.path }`)
		})
		this.shortcutMap.set('Control.Shift.^', () => {
			this.routerService.navigateByUrl(`/${ config.modules.costInvoices.path }`)
		})
		this.shortcutMap.set('Control.Shift.7', () => {
			this.routerService.navigateByUrl(`/${ config.modules.cashDocuments.path }/cash-receipts`)
		})
		this.shortcutMap.set('Control.Shift.8', () => {
			this.routerService.navigateByUrl(`/${ config.modules.cashDocuments.path }/cash-disbursement`)
		})
		this.shortcutMap.set('Control.Shift.9', () => {
			this.routerService.navigateByUrl(`/${ config.modules.settings.path }`)
		})
		this.shortcutMap.set('Control.Shift.0', () => {
			this.routerService.navigateByUrl(`/${ config.modules.chat.path }`)
		})*/
		/*(**)*/
		this.loaderService.withoutFadeInOnEnterAnimation().enable()
		this.languageService.initLanguages(config.availableLanguages)
		this.menuService.setMenuWidth({ expanded: 250, collapsed: 64 })
		this.appService.internalServerErrorChanges().subscribe(value => {
			this.internalServerError = value
		})
		const subscription: Subscription = this.authService.afterLoggingIn.subscribe(value => {
			if (value) {
				this.socketsService.connect()
				subscription.unsubscribe()
			}
		})
		this.openUserRegulationsModal()
		this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
			if (authService.getUserIdFromToken()) {
				this.openUserRegulations()
			}
		})
		this.refreshJwtToken()
	}

	@HostListener('window:keydown.control.shift.l', ['$event'])
	openCommandLine(event: KeyboardEvent): void {
		event.preventDefault()
		this.showCommandLine = true
	}

	ngOnInit() {
		this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
			this.modalService.closeAll()
			if (!this.router.url.startsWith('/orders') || this.router.url.includes('create')) {
				sessionStorage.removeItem(OrderListScrollStorageKey)
			}
			if (!this.router.url.startsWith('/cash-documents/cash-disbursement') || this.router.url.includes('create')) {
				sessionStorage.removeItem(CashDisbursementsListScrollStorageKey)
			}
			if (!this.router.url.startsWith('/cash-documents/cash-receipts') || this.router.url.includes('create')) {
				sessionStorage.removeItem(CashReceiptsListScrollStorageKey)
			}
			if (!this.router.url.startsWith('/cash-documents/transfer-incoming') || this.router.url.includes('create')) {
				sessionStorage.removeItem(TransferListScrollStorageKey)
			}
			if (!this.router.url.startsWith('/invoices') || this.router.url.includes('create')) {
				sessionStorage.removeItem(InvoiceListScrollStorageKey)
			}
			if (!this.router.url.startsWith('/cost-invoices') || this.router.url.includes('create')) {
				sessionStorage.removeItem(CostInvoiceListScrollStorageKey)
			}
			if (!this.router.url.startsWith('/mailbox') || this.router.url.includes('create')) {
				sessionStorage.removeItem(MailListScrollStorageKey)
			}
		})
		this.chatService.initSockets()
	}

	async handleCommand(command: string): Promise<void> {
		this.showCommandLine = false

		switch (command) {
			case 'reload': {
				window.location.reload()
				break
			}
			case '!info': {
				const modal = await this.modalService.open(AppInfoComponent)
				modal.title = $localize`App Info`
				modal.iconName = 'settings'
				break
			}
			case '!500': {
				this.internalServerError = true
				break
			}
			default: {
				this.notificationService.error('Unknown command')
			}
		}
	}

	openUserRegulations() {
		if (sessionStorage.getItem('acceptance_of_the_regulations') !== 'ACCEPTED') {
			this.equipmentsService.getEquipmentUsePolicyAcceptanceForCurrentUser().subscribe({
				next: (response: EquipmentUsePolicyAcceptanceRead) => {
					if (response.user.regulations_status === 'REJECTED') {
						this.openUserRegulationsModal()
					}
				},
				error: () => {
					this.notificationService.error($localize`Failed to retrieve equipment use policy acceptance`)
				},
			})
		}
	}

	async openUserRegulationsModal() {
		const modal = await this.modalService.open(RegulationsForm)
		modal.title = 'Rules'
		modal.iconName = 'show-all'
		modal.width = '1000px'

		this.modalService.modalRef.closed.subscribe((confirmed: boolean) => {
			if (confirmed) {
				sessionStorage.setItem('acceptance_of_the_regulations', 'ACCEPTED')
			}
		})
	}

	changeLanguage(language: LanguageCode) {
		this.languageService.setAppLanguage(language)
	}

	private refreshJwtToken(): void {
		const logger: Logger = new Logger('Refresh JWT token')

		const refresh = () => {
			if (this.authService.tokenIsSet()) {
				if (this.authService.refreshToken) {
					logger.info('Refreshing JWT token')
					this.authService.getToken(TokenRequest.withRefreshToken(this.authService.refreshToken)).subscribe({
						next: (response: TokenResponse) => {
							logger.info('JWT token has been refreshed')
							this.authService.saveToken(response.token, response.refresh_token)
							this.socketsService.connect()
						},
					})
				}
			}
		}

		refresh()

		setInterval(() => {
			refresh()
		}, 300_000)
	}
}
